import { TopPage } from "@/components/elements/TopPage";
import { getDynamicRoute } from "@/utils/url";
import { Stack, styled } from "@mui/material";
import { FC, useMemo, useState, useEffect, useRef } from "react";
import { matchRoutes, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { getMatchMenu } from "@/features/match/constants/matchMenu";
import { GenericTabs } from "@/components/commons/GenericTabs";
import { blue } from "@/theme/colors";
import { InnerBox } from "@/components/elements/InnerBox";
import { useCountContractStatusCount } from "@/api/services/admin/match";
import { useMatchListSessionStorage } from "@/hooks/useMatchListSessionStorage";
import { useMatchSortValidation } from "@/features/match/hooks/use-match-sort-validation";
import { useIsPageParamValid, useSanitizedPageParam } from "@/hooks/useSanitizedPageParam";

type TMatchLayoutProps = {};

const MATCH_TAB_PATHS = [
  AppRoutes.duringContractList,
  AppRoutes.canceledContractList,
  AppRoutes.trialMatchList,
  AppRoutes.trialCancelledList,
] as const;

export const MatchLayout: FC<TMatchLayoutProps> = () => {
  useSanitizedPageParam();

  const { id } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { saveState, getState } = useMatchListSessionStorage();
  const isValidPage = useIsPageParamValid();

  const { isValidSort } = useMatchSortValidation();

  const { data: contractStatusCount } = useCountContractStatusCount();

  const matchMenu = useMemo(() => getMatchMenu(contractStatusCount?.data), [contractStatusCount]);

  const activeIndex = matchMenu.findIndex(
    ({ url, extraUrls }) =>
      matchRoutes([{ path: url }], pathname) !== null || extraUrls?.some((extraUrl) => matchRoutes([{ path: extraUrl }], pathname) !== null),
  );
  const [value, setValue] = useState(activeIndex !== -1 ? activeIndex : 0);

  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const isTabSwitch = prevPathnameRef.current !== null && prevPathnameRef.current !== pathname;
    const isInitialLoad = prevPathnameRef.current === null;
    prevPathnameRef.current = pathname;

    if (!isTabSwitch && !isInitialLoad) return;
    if (!isValidSort || !isValidPage) return;
    if (!MATCH_TAB_PATHS.includes(pathname)) return;

    const savedState = getState();
    if (!savedState?.searchParams) return;
    if (search) return;

    if (savedState.activeTabUrl === pathname) {
      navigate(`${pathname}?${savedState.searchParams}`, { replace: true });
    }
  }, [pathname, search, navigate, getState, isValidSort, isValidPage]);

  useEffect(() => {
    if (!isValidSort || !isValidPage) return;
    if (MATCH_TAB_PATHS.includes(pathname)) {
      saveState({
        activeTabUrl: pathname,
        searchParams: search.startsWith("?") ? search.slice(1) : search,
      });
    }
  }, [pathname, search, saveState, isValidSort, isValidPage]);

  useEffect(() => {
    if (activeIndex !== -1 && activeIndex !== value) {
      setValue(activeIndex);
    }
  }, [activeIndex, pathname]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const isExtraUrlActive = useMemo(() => {
    return matchMenu.some(({ extraUrls }) => extraUrls?.some((extraUrl) => matchRoutes([{ path: extraUrl }], pathname) !== null));
  }, [pathname]);

  const backUrl = useMemo(() => {
    if (isExtraUrlActive) {
      const activeMenu = matchMenu.find(({ extraUrls }) => extraUrls?.some((extraUrl) => matchRoutes([{ path: extraUrl }], pathname) !== null));
      return activeMenu ? getDynamicRoute(activeMenu.url, { id }) : AppRoutes.duringContractList;
    }
    return AppRoutes.duringContractList;
  }, [isExtraUrlActive, pathname, id]);

  return (
    <Stack gap={2.5} sx={{ mt: 1 }}>
      {isExtraUrlActive ? (
        <TopPage
          title="紹介会社一覧"
          description="システムに登録されている紹介会社の一覧です。新規登録する場合は「新規紹介会社登録」で追加した後に顧客ユーザーを登録してください。"
          backUrl={backUrl}
        />
      ) : (
        <TopPage
          title="紹介会社一覧"
          description="システムに登録されている紹介会社の一覧です。新規登録する場合は「新規紹介会社登録」で追加した後に顧客ユーザーを登録してください。"
          enablePageBack={false}
        />
      )}
      <GenericTabs
        colorBorder={blue[80]}
        tabs={matchMenu.map(({ title, url }) => ({ label: title, url }))}
        value={value}
        onChange={handleChange}
        id={id}
      />
      <OutletBox>
        <Outlet />
      </OutletBox>
    </Stack>
  );
};

const OutletBox = styled(InnerBox)`
  margin-top: -20px;
  padding-top: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;
